import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import { CheckCircle, Star, Users } from 'lucide-react';

const StudyNotesCloudOpsPage = () => {
  const [expandedModules, setExpandedModules] = useState({});

  const toggleModule = (index) => {
    setExpandedModules(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const courseModules = [
    {
      title: "Introduction",
      lessons: [
        "Overview & Exam Structure",
        "[PDF Summary] AWS Certified CloudOps Associate Study Notes"
      ]
    },
    {
      title: "Domain 1: Monitoring, Logging, Analysis, Remediation & Performance Optimization",
      lessons: [
        "Prerequisites: CloudOps & AWS Operations Fundamentals",
        "Implement metrics, alarms, and filters using AWS monitoring and logging services",
        "Identify and remediate issues using monitoring and availability metrics",
        "Implement performance optimization strategies for compute, storage, and databases",
        "Example Scenario - 1"
      ]
    },
    {
      title: "Domain 2: Reliability and Business Continuity",
      lessons: [
        "Implement scalability and elasticity",
        "Implement highly available and resilient environments",
        "Implement backup and restore strategies",
        "Example Scenario - 2"
      ]
    },
    {
      title: "Domain 3: Deployment, Provisioning, and Automation",
      lessons: [
        "Provision and maintain cloud resources",
        "Automate management of existing resources",
        "Implement automated provisioning",
        "Example Scenario - 3"
      ]
    },
    {
      title: "Domain 4: Security and Compliance",
      lessons: [
        "Implement and manage security and compliance tools and policies",
        "Implement strategies to protect data and infrastructure",
        "Example Scenario - 4"
      ]
    },
    {
      title: "Domain 5: Networking and Content Delivery",
      lessons: [
        "Implement and optimize networking features and connectivity",
        "Configure domains, DNS, and content delivery",
        "Troubleshoot network connectivity issues",
        "Example Scenario - 5"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PromoBanner />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Course Info */}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-cyan-600 mb-6">
                  Study Notes: AWS CloudOps Engineer Associate
                </h1>
                
                <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                  Complete Study Notes to help you ace your AWS CloudOps Engineer Associate Certification Exam.
                </p>
                
                <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                  Use these notes as the Ultimate Revision Guide. Detailed explanations, visual diagrams, analogies and more!
                </p>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Perfect for revision, or as a first-time learning guide for aspiring CloudOps engineers.
                </p>
                
                <div className="flex items-center gap-2 mb-8">
                  <Users size={20} className="text-blue-500" />
                  <span className="text-lg font-medium text-gray-700">Join hundreds of students!</span>
                  <Star size={20} className="text-yellow-500 fill-current" />
                </div>
                
                <a 
                  href="https://learn.zerotocloud.co/p/study-notes-aws-certified-cloudops-associate"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block text-center"
                >
                  Enroll now for $39
                </a>
              </div>
              
              {/* Right Side - Course Thumbnail */}
              <div className="relative">
                <img 
                  src="/course-covers/study-notes-cloudops.png"
                  alt="Study Notes: AWS CloudOps Engineer Associate"
                  className="w-full rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-cyan-600 mb-12 text-center">
              Course Content
            </h2>
            
            <div className="space-y-4">
              {courseModules.map((module, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                  <button
                    onClick={() => toggleModule(index)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-all duration-200 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-blue-600 font-semibold text-lg">
                        {index + 1}.
                      </span>
                      <h3 className="text-gray-900 font-medium">
                        {module.title}
                      </h3>
                    </div>
                    <div className="text-blue-600">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className={`transform transition-transform duration-200 ${expandedModules[index] ? 'rotate-90' : ''}`}>
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </button>
                  
                  {expandedModules[index] && (
                    <div className="px-4 pb-4 bg-gray-50 border-t border-gray-200">
                      <ul className="space-y-3 pt-4">
                        {module.lessons.map((lesson, lessonIndex) => (
                          <li key={lessonIndex} className="flex items-start gap-3">
                            <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                            <span className="text-gray-700">{lesson}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Start Learning Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-cyan-600 mb-6">
              Ready to start learning?
            </h2>
            
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="mb-6">
                <div className="text-4xl font-bold text-gray-900 mb-2">$39</div>
                <p className="text-gray-600">One-time payment • Lifetime access</p>
              </div>
              
              <a 
                href="https://learn.zerotocloud.co/p/study-notes-aws-certified-cloudops-associate"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-xl hover:shadow-lg transition-all duration-200 mb-4 inline-block text-center"
              >
                Enroll now for $39
              </a>
              
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Users size={16} className="text-blue-500" />
                <span>Join hundreds of students already enrolled</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudyNotesCloudOpsPage;